import type { AccountTab } from "./portal-navigation";
export type TourStep = {
  title: string;
  text: string;
  notes: string[];
  section: string;
  tab?: AccountTab;
  action: string;
};

export function portalTourSteps(admin: boolean): TourStep[] {
  return admin
    ? [
        {
          title: "Start at the Dashboard",
          text: "Review work that needs attention and continue customer setup.",
          notes: [
            "Open a queue to review inquiries, account messages, port corrections or flagged service orders.",
            "Customer setup links go directly to the account, Numbers or Equipment.",
            "Totals describe saved portal records, not live calls or working phone service.",
          ],
          section: "overview",
          action: "Open Dashboard",
        },
        {
          title: "Start with the customer",
          text: "Customers is the starting point for service setup.",
          notes: [
            "Add a customer with their contact details and physical service address.",
            "Select a matching plan during signup or later from the account overview. This does not start billing.",
            "Choose a number near that address, search another area, or record an existing number to keep.",
            "Saving a number preference does not purchase or transfer it.",
          ],
          section: "customers",
          action: "Open Customers",
        },
        {
          title: "Use a number you already own",
          text: "Open a customer's Numbers tab, then Add existing number.",
          notes: [
            "The pool includes connected Flowroute accounts and Telnyx.",
            "Select an eligible number and confirm the assignment. Ownership is checked again before saving.",
            "This links the portal account; it does not change where the number rings.",
          ],
          section: "account",
          tab: "numbers",
          action: "Open customer Numbers",
        },
        {
          title: "Register the phone or adapter",
          text: "Equipment keeps the device and its physical location together.",
          notes: [
            "Add the model and MAC address printed on the device.",
            "Choose its service location. Staff can correct that location later.",
            "For commercial accounts, open Extensions to add internal numbers and request equipment, voicemail and call preferences. Saved requests are not yet applied to the phone system.",
            "For the Cache River company account, assign a Tailscale softphone to an unused 6001–6999 extension, excluding 6200, then open its Manual phone setup to install it.",
            "Existing company softphones can issue their current connection details without rotating passwords. Each reveal checks the carrier address and installed emergency route; it does not place an emergency call.",
          ],
          section: "account",
          tab: "devices",
          action: "Open customer Equipment",
        },
        {
          title: "Review the emergency address",
          text: "Use the location where the phone is actually used, even when its number has another area code.",
          notes: [
            "Review the street address, city, state and ZIP code.",
            "Correcting an address clears its previous local validation.",
            "Open Emergency service at the carrier to check a linked Flowroute number against this exact location. Each result is dated.",
            "A saved address is not active E911 service. Carrier and phone-system verification are separate requirements.",
          ],
          section: "account",
          tab: "overview",
          action: "Open account overview",
        },
        {
          title: "Apply call handling",
          text: "Features separates saved preferences from the settings on the phone server.",
          notes: [
            "Select the phone or extension, save its preferences, then apply call handling.",
            "Review the server receipt for forwarding, Do Not Disturb, ring time, call waiting and voicemail.",
            "Voicemail lists the installed mailbox, messages and its private PIN. Dial *97 from that line to manage messages or change the PIN.",
            "Three-way calling and caller ID still need device and carrier verification; a saved checkbox is not a successful phone test.",
          ],
          section: "account",
          tab: "features",
          action: "Open customer Features",
        },
        {
          title: "Create a greeting",
          text: "Recordings turns your text into a downloadable telephone greeting or menu.",
          notes: [
            "Open the customer's Recordings tab and choose the purpose and voice. Company recordings are in Voice studio.",
            "Write the greeting, generate it, and listen before downloading.",
            "Generating a recording does not install it in a voicemail box or phone menu.",
          ],
          section: "account",
          tab: "recordings",
          action: "Open customer Recordings",
        },
        {
          title: "Check the customer's experience",
          text: "View as customer opens a timed, read-only customer view under your staff identity.",
          notes: [
            "Open it from the account overview and record why you need access.",
            "Use Customer access to create a private invitation for the account holder.",
            "Exit customer view before making staff changes. The tour itself changes no account records.",
          ],
          section: "account",
          tab: "overview",
          action: "Open customer view controls",
        },
      ]
    : [
        {
          title: "Your account at a glance",
          text: "The overview shows your account details, service address and setup records.",
          notes: [
            "Check the physical address where you use your phone.",
            "Emergency service at the carrier checks a linked Flowroute number against this saved address. It does not test emergency calling from your phone.",
            "Your selected plan appears here. Selected for setup does not mean service is active.",
            "Contact Cache River through Messages if it needs correcting.",
            "A saved record does not by itself mean calling is active.",
          ],
          section: "account",
          tab: "overview",
          action: "Open my overview",
        },
        {
          title: "Find your telephone number",
          text: "Numbers keeps your assigned numbers and transfer requests together.",
          notes: [
            "Check the status beside your number.",
            "A transfer request can be saved before it has been submitted to a carrier.",
            "Do not cancel existing service while a number transfer is still being arranged.",
          ],
          section: "account",
          tab: "numbers",
          action: "Open my Numbers",
        },
        {
          title: "Set up your phone",
          text: "Equipment shows the phones and adapters registered to your account.",
          notes: [
            "Match the model and MAC address to the device you are connecting.",
            "Commercial account holders can use Extensions to add internal numbers. Use Features to save and apply supported call-handling preferences to an installed phone.",
            "Open Manual phone setup for that device when connection details are ready.",
            "If details are held for emergency-address verification, contact Cache River. Do not use another device's settings.",
          ],
          section: "account",
          tab: "devices",
          action: "Open my Equipment",
        },
        {
          title: "Manage calls and voicemail",
          text: "Features controls how an installed phone handles incoming calls.",
          notes: [
            "Save your preferences, then apply call handling and check the result from the phone server.",
            "Voicemail shows whether the mailbox is enabled and lists your recorded messages.",
            "Reveal your voicemail PIN privately. Dial *97 from your phone to listen, save, delete or change the PIN.",
            "Forwarding takes priority over voicemail. Do Not Disturb returns busy.",
          ],
          section: "account",
          tab: "voicemail",
          action: "Open my Voicemail",
        },
        {
          title: "Record your greeting",
          text: "Recordings creates voicemail greetings and phone menus from your text.",
          notes: [
            "Choose a voice and speaking speed, then generate the recording.",
            "You can leave and return while it is being generated. Completed files are saved to your account.",
            "Listen and download the WAV file. Saving it does not change a live greeting or menu.",
          ],
          section: "account",
          tab: "recordings",
          action: "Open my Recordings",
        },
        {
          title: "Check your bills",
          text: "Billing shows the invoices recorded for your account and their status.",
          notes: [
            "An empty list means no invoices are recorded in this portal.",
            "Complimentary accounts are identified separately from standard plans.",
            "Use Messages for a billing question; do not send payment-card details.",
          ],
          section: "account",
          tab: "billing",
          action: "Open my Billing",
        },
        {
          title: "Ask for help",
          text: "Messages keeps your questions and the replies with your account.",
          notes: [
            "Describe the affected number or phone and what happened.",
            "Keep passwords, account PINs and payment details out of the message.",
            "The message inbox is not an emergency service.",
          ],
          section: "messages",
          action: "Open Messages",
        },
      ];
}
